import { type ReactElement, useCallback, useEffect, useState } from "react";

import crmEnrichmentTrackingService from "app/pages/sales-intelligence/modules/crm-enrichment/services/tracking/trackingService";
import { CrmIndustryTransitionAlertBanner } from "app/pages/sales-intelligence/modules/industry/components/CrmIndustryTransitionAlertBanner/CrmIndustryTransitionAlertBanner";
import { isCrmJobsTransitionProvider } from "app/pages/sales-intelligence/modules/industry/types/crmJobsTransition";
import type { CrmEnrichmentJob } from "app/pages/sales-intelligence/modules/crm-enrichment/types/types";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

import { MAX_JOBS_QUANTITY } from "../../constants/enrichment";
import { EnrichmentSection } from "../EnrichmentSection/EnrichmentSection";
import EnrichmentSectionActions from "../EnrichmentSection/EnrichmentSectionActions";
import EnrichmentSectionLoader from "../EnrichmentSectionLoader/EnrichmentSectionLoader";

import type { BaseEnrichmentPreferencesProps } from "./types";

const BaseEnrichmentPreferences = ({
    integrationName,
    isMappingLoading,
    onEditMapping,
    queryResult,
    slots,
}: BaseEnrichmentPreferencesProps) => {
    const { data, isLoading } = queryResult;
    const { renderJobToggle: JobToggle, renderModals, renderEmptyState: EmptyState } = slots;

    const [activeJob, setActiveJob] = useState<CrmEnrichmentJob | null>(null);

    const clearActiveJob = useCallback(() => {
        setActiveJob(null);
    }, []);

    useEffect(() => {
        clearActiveJob();
    }, [data, clearActiveJob]);

    const onClickAddJob = useCallback(() => {
        crmEnrichmentTrackingService.trackCreateJobClick(data?.length ?? 0, integrationName);

        void siNavigator.goToCrmEnrichmentManagementPage({ crm: integrationName });
    }, [data?.length, integrationName]);

    const isJobNumberLimitReached = !!data && data.length >= MAX_JOBS_QUANTITY;
    const isCreateJobDisabled = isLoading || !data || isJobNumberLimitReached;
    const hasJobs = !!data?.length;

    const sectionActions = hasJobs ? (
        <EnrichmentSectionActions
            integration={integrationName}
            isTooltipEnabled={isJobNumberLimitReached}
            isCreateJobDisabled={isCreateJobDisabled}
            onCreateJob={onClickAddJob}
        />
    ) : null;

    const renderContent = (): ReactElement => {
        if (isLoading) {
            return <EnrichmentSectionLoader />;
        }

        if (hasJobs && data) {
            return (
                <>
                    {data.map((job) => (
                        <JobToggle
                            job={job}
                            onRenameClick={setActiveJob}
                            onDeleteClick={setActiveJob}
                        />
                    ))}

                    {activeJob
                        ? renderModals({
                              activeJob,
                              onClearActiveJob: clearActiveJob,
                          })
                        : null}
                </>
            );
        }

        return (
            <EmptyState
                isMappingLoading={isMappingLoading}
                isCreateJobDisabled={isCreateJobDisabled}
                onClickAddJob={onClickAddJob}
                onEditMapping={onEditMapping}
            />
        );
    };

    return (
        <EnrichmentSection
            integration={integrationName}
            actions={sectionActions}
        >
            {isCrmJobsTransitionProvider(integrationName) ? (
                <CrmIndustryTransitionAlertBanner
                    crm={integrationName}
                    variant="crmJobs"
                />
            ) : null}
            {renderContent()}
        </EnrichmentSection>
    );
};

export default BaseEnrichmentPreferences;
